/**
 * Semantic-validation rules the FE checks on blur for ShortText fields with a
 * `validate` property set. Each entry pairs the rule name (matches the BE
 * allow-list in WorkflowField.cs → IsValidValidateRule) with a runtime check
 * that returns a translated error message key when the value is wrong, or
 * null when it's fine.
 *
 * Empty / whitespace values short-circuit to null — "required" is enforced
 * separately by the existing required-flag UX. A blank optional SIN, for
 * example, should not surface a "must be 9 digits" error.
 */

export type ValidateRule = 'sin' | 'email' | 'phone' | 'postal' | 'regex';

export type ValidationResult = { errorKey: string } | null;

export function runValidate(
  rule: ValidateRule | undefined | null,
  raw: string,
  // Pattern is only consulted for the 'regex' rule; the originator authors it
  // on the field. Ignored by every other rule.
  pattern?: string | null,
): ValidationResult {
  if (!rule) return null;
  const value = (raw ?? '').trim();
  if (value === '') return null;
  switch (rule) {
    case 'sin':
      return validateSin(value);
    case 'email':
      return validateEmail(value);
    case 'phone':
      return validatePhone(value);
    case 'postal':
      return validatePostal(value);
    case 'regex':
      return validateRegex(value, pattern);
    default:
      return null;
  }
}

/**
 * Originator-authored regex rule. The value must match the whole authored
 * pattern. A pattern that fails to compile (should never happen — the builder +
 * BE both reject invalid patterns) fails OPEN: we treat the value as valid
 * rather than block the advisor on a config error. The generic errorKey is a
 * fallback; the renderer prefers the field's authored bilingual message when set.
 */
function validateRegex(value: string, pattern: string | null | undefined): ValidationResult {
  const source = (pattern ?? '').trim();
  if (source === '') return null;
  let re: RegExp;
  try {
    re = new RegExp(source);
  } catch {
    return null;
  }
  return re.test(value) ? null : { errorKey: 'formRenderer.validation.regex.format' };
}

/**
 * Canadian SIN — 9 digits, Luhn checksum, first digit 1–7 or 9.
 *   - 0 unused.
 *   - 8 only appears on locally-issued cards in specific provinces; for the
 *     general account-opening flow we treat 8 as invalid to flag typos.
 * Strips spaces and hyphens before checking — copy/paste from a SIN card
 * (XXX-XXX-XXX) is forgiving.
 */
function validateSin(value: string): ValidationResult {
  const digits = value.replace(/[\s-]/g, '');
  if (!/^[0-9]{9}$/.test(digits)) return { errorKey: 'formRenderer.validation.sin.length' };
  // Test-data escape hatch: `000 000 000` always validates so QA flows that
  // exercise downstream steps without a real SIN aren't blocked here. The
  // Luhn check would actually pass on all-zeros (sum is 0, divisible by 10)
  // — the first-digit rule is what rejects it. Keep the rule otherwise.
  if (digits === '000000000') return null;
  const first = digits.charCodeAt(0) - 48;
  if (first === 0 || first === 8) return { errorKey: 'formRenderer.validation.sin.firstDigit' };
  if (!luhnPasses(digits)) return { errorKey: 'formRenderer.validation.sin.checksum' };
  return null;
}

/**
 * Mod-10 Luhn checksum. Standard algorithm: walk right-to-left, double every
 * second digit, sum-of-digits when the doubled value > 9, total must be a
 * multiple of 10.
 */
function luhnPasses(digits: string): boolean {
  let sum = 0;
  let doubleIt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (doubleIt) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    doubleIt = !doubleIt;
  }
  return sum % 10 === 0;
}

/**
 * Email — pragmatic check, not RFC 5322. Looks for `local@domain.tld` shape
 * with no whitespace. Catches the obvious typos (missing @, missing TLD)
 * without rejecting unusual-but-valid addresses.
 */
function validateEmail(value: string): ValidationResult {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
    return { errorKey: 'formRenderer.validation.email.format' };
  }
  return null;
}

/**
 * Phone — exactly 10 digits after stripping common separators (space, dash,
 * parens, dot). North-American format; no country code expected.
 */
function validatePhone(value: string): ValidationResult {
  const digits = value.replace(/[\s\-().]/g, '');
  if (!/^[0-9]{10}$/.test(digits)) {
    return { errorKey: 'formRenderer.validation.phone.length' };
  }
  return null;
}

/**
 * Canadian postal code — A1A 1A1 pattern, case-insensitive, optional middle
 * space. Canada Post forbids D / F / I / O / Q / U in every letter position,
 * plus W and Z in the leading position. We enforce the same restricted
 * alphabet in all three letter slots so an invalid `M5D 3F8` doesn't pass
 * the regex; downstream services still do existence checks for assignments
 * that aren't currently in use.
 */
function validatePostal(value: string): ValidationResult {
  const compact = value.replace(/\s/g, '').toUpperCase();
  // Position 1: A-Z minus D F I O Q U W Z (canada-post specific).
  // Positions 3 + 5: A-Z minus D F I O Q U (W and Z are allowed mid-code).
  if (
    !/^[ABCEGHJKLMNPRSTVXY][0-9][ABCEGHJKLMNPRSTVWXYZ][0-9][ABCEGHJKLMNPRSTVWXYZ][0-9]$/.test(
      compact,
    )
  ) {
    return { errorKey: 'formRenderer.validation.postal.format' };
  }
  return null;
}

// ---- Input formatting masks. Run on every keystroke. Each mask strips its
// own separators from the input first, caps to the rule's max-digit/char
// count, then re-inserts separators at fixed positions. The result is what
// the advisor sees AND what we store — runValidate strips the separators
// again before the semantic check, so storage/validation stay aligned.

export type MaskedRule = 'sin' | 'phone' | 'postal';

/** True if the rule has a paired format mask. SIN / phone / postal do; email
 *  doesn't (no canonical fixed shape). */
export function isMaskedRule(rule: string | null | undefined): rule is MaskedRule {
  return rule === 'sin' || rule === 'phone' || rule === 'postal';
}

/**
 * Format a ShortText input value according to its `validate` rule. Returns
 * the input untouched when the rule has no mask. The caller is expected to
 * skip `applyInputRestriction` when a mask is in play — the mask already
 * filters the character set.
 */
export function applyFormatMask(rule: string | null | undefined, raw: string): string {
  if (!isMaskedRule(rule)) return raw;
  if (rule === 'sin') return maskSin(raw);
  if (rule === 'phone') return maskPhone(raw);
  return maskPostal(raw);
}

/** "123 456 789" — three groups of three digits separated by single spaces. */
function maskSin(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`;
}

/** "(416) 555-1234" — North-American 10-digit phone with parens / dash. The
 *  leading `(` lands as soon as the advisor types the first digit so the
 *  intent of the field is obvious. */
function maskPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** "M5V 3A8" — 6 alphanumeric characters with a single space after the
 *  third. Strips anything that isn't a letter or digit (pasted hyphens /
 *  punctuation no longer leak into letter slots) and auto-uppercases so the
 *  advisor doesn't have to think about case. */
function maskPostal(raw: string): string {
  const c = raw
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase()
    .slice(0, 6);
  if (c.length <= 3) return c;
  return `${c.slice(0, 3)} ${c.slice(3)}`;
}

/**
 * Whether a Date field's value satisfies its `minAge` rule.
 *
 * The renderer already greys out-of-range days via the picker's
 * `disabledDate`, but AntD's DatePicker deliberately accepts TYPED input
 * (see the `dateFormat` comment in FieldRenderer) — so the calendar alone is
 * cosmetic. Typing a future date, or a date under the minimum age, was
 * accepted and saved. This is the enforcement half.
 *
 * `minAge: 0` means "no future dates" (the cutoff is today), which is how the
 * FCCP "Date of Formation" rule is expressed without a second property.
 *
 * Empty values short-circuit — "required" is enforced separately, so a blank
 * optional date must not surface an age error.
 *
 * @param raw   ISO `YYYY-MM-DD` as stored by the Date renderer.
 * @param today Injectable for tests; defaults to the current date.
 */
export function validateMinAge(
  minAge: number | null | undefined,
  raw: string | null | undefined,
  today: Date = new Date(),
): ValidationResult {
  if (minAge === null || minAge === undefined) return null;
  const value = (raw ?? '').trim();
  if (value === '') return null;

  const parts = value.split('-').map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || !y || !m || !d || parts.some((n) => !Number.isFinite(n))) {
    return null; // Unparseable — the picker owns format errors.
  }

  // Cutoff = today minus minAge years. Compared date-only, so a birthday
  // falling exactly on the cutoff passes (you ARE 18 that day).
  const cutoff = new Date(today.getFullYear() - minAge, today.getMonth(), today.getDate());
  const entered = new Date(y, m - 1, d);

  if (entered.getTime() > cutoff.getTime()) {
    return {
      errorKey:
        minAge === 0
          ? 'formRenderer.validation.date.noFuture'
          : 'formRenderer.validation.date.minAge',
    };
  }
  return null;
}

/**
 * True when a CheckboxGroup's current selection satisfies its authored
 * `minSelections` floor.
 *
 * `required` on a CheckboxGroup only ever meant "non-empty", so an
 * acknowledgement gate — RESP's three eligibility cards, where the source
 * form disables Next until every card is ticked — could not be expressed:
 * ticking one card let the advisor continue. This is the cardinality half.
 *
 * Note the polarity difference from `blockingValues`, which fires when a
 * value IS selected and can never fire on an empty selection. That rule
 * stops a workflow on a disqualifying answer; this one holds it until
 * enough boxes are ticked.
 *
 * Fields without a floor, and non-array values, pass — the caller's
 * `required` / `isFilled` check owns those cases.
 */
export function meetsMinSelections(
  minSelections: number | null | undefined,
  value: unknown,
): boolean {
  if (typeof minSelections !== 'number' || !Number.isFinite(minSelections)) return true;
  if (minSelections < 1) return true;
  if (!Array.isArray(value)) return false;
  return value.length >= minSelections;
}
